/* [D-560] «Повна сторінка юзера»: ліворуч — функції цього юзера, праворуч — зафіксований телефон зі Студією v-5 ЙОГО очима.

   OWNER 13.10: «в одну колонку зроблений весь список функцій конкретного юзера зліва а справа все вільно, то я би хотів щоб справа було
   зафіксоване прев'ю телефона який так само буде показувати у реальному часі всі зміни які будуть на екрані юзера по принципу як у нас
   це зроблено коли я натискаю у партнера (Дозволи і теми)».

   Телефон — та сама справжня Студія в демо-режимі, що й у «🧪 Дозволи і Теми» (/miniapp/studio?demo=<партнер>), але з ?u=<юзер>:
   сервер рахує дозволи саме цього юзера (особисте → партнер → «усім учням» → усім → довідник) і його межу версій. Після кожної зміни
   на картці нові дозволи йдуть у телефон повідомленням (без перезавантаження), а блок функції блимає жовтим. У телефоні нічого не
   зберігається й не запускається. Студії в юзера немає (не учень партнера) — замість телефона пояснення, що з цим робити. */
import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import api from '../api';
import FeatureRows, { FeatureLegend } from './FeatureRows';
import { helpFor } from './featureHelp';

const C = { neon: '#00e5a0', neonTxt: '#7ffcd3', dim: '#a8bece', txt: '#eaf6ff', gold: '#ffd166', coral: '#ff7a59', line: 'rgba(0,229,160,.25)' };
const DEVICES = [['📱', 'iPhone', 390, 844], ['🤖', 'Android', 412, 915], ['📱', 'Малий телефон', 360, 740]];
const IN_STUDIO = /^(v5_|subs$|mirror$|fx_outro$)/;   // функції, що мають власний блок у Студії (як у «Дозволи і Теми»)
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('uk-UA') : '—');

export default function UserStudioPhone({ userId, rows, onSet, busyKey, change, ver }) {
  const [demo, setDemo] = useState(null);     // відповідь /users/{id}/studio-demo; null — ще вантажиться; false — помилка
  const [n, setN] = useState(0);              // лічильник перезавантажень телефона
  const [dev, setDev] = useState(0);
  const [picked, setPicked] = useState('');
  const [last, setLast] = useState('');
  const [vh, setVh] = useState(typeof window !== 'undefined' ? window.innerHeight : 900);
  const frame = useRef(null);
  const rowEl = useRef(null), sideEl = useRef(null);
  const rowsRef = useRef(null); rowsRef.current = rows;
  const pend = useRef(null);                  // { key, state } — що щойно змінили (для підпису після підсвітки)
  const seq = useRef(0);
  const [, , devW, devH] = DEVICES[dev];
  const scale = Math.max(0.45, Math.min(1, (vh - 150) / devH));

  useEffect(() => {
    const on = () => setVh(window.innerHeight);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);

  // «Зафіксований» телефон. CSS position: sticky тут не працює: <main> дашборду має overflow-x: hidden, тобто сам є вікном прокрутки,
  // яке ніколи не гортається (гортається сторінка). Тому зсуваємо колонку самі — рівно настільки, наскільки блок заїхав під верх екрана.
  useEffect(() => {
    let raf = 0;
    const place = () => {
      raf = 0;
      const row = rowEl.current, side = sideEl.current;
      if (!row || !side) return;
      let off = 0;
      if (window.innerWidth >= 900) {
        const r = row.getBoundingClientRect();
        off = Math.max(0, Math.min(12 - r.top, r.height - side.offsetHeight));
      }
      side.style.transform = off > 0 ? 'translateY(' + Math.round(off) + 'px)' : '';
    };
    const on = () => { if (!raf) raf = window.requestAnimationFrame(place); };
    window.addEventListener('scroll', on, true); window.addEventListener('resize', on);
    place();
    const iv = setInterval(place, 800);   // висота лівої колонки міняється (інший розділ, пошук) — підправляємо
    return () => { window.removeEventListener('scroll', on, true); window.removeEventListener('resize', on); clearInterval(iv); if (raf) window.cancelAnimationFrame(raf); };
  }, []);

  const fetchDemo = () => api.get(`/users/${userId}/studio-demo`).then(r => r.data);
  useEffect(() => {
    if (!userId) return;
    setDemo(null); setLast(''); setPicked('');
    fetchDemo().then(setDemo).catch(() => setDemo(false));
  }, [userId]);

  // [D-691] «🧭 Версії» змінили показ редакторів (напр. відкрили Студію) - телефон перечитує і перемальовується одразу
  const firstVer = useRef(true);
  useEffect(() => {
    if (firstVer.current) { firstVer.current = false; return; }
    fetchDemo().then(d => { setDemo(d); setN(x => x + 1); }).catch(() => {});
  }, [ver]);

  const who = (demo && demo.user && demo.user.who) || 'юзер';
  const whoRef = useRef(who); whoRef.current = who;

  // відповідь Студії: «блок підсвічено» чи «такого блоку немає» → підпис словами
  useEffect(() => {
    const on = (e) => {
      if (e.origin !== window.location.origin || !e.data || e.data.type !== 'st-flashed') return;
      const f = (rowsRef.current || []).find(x => x.key === e.data.key) || { key: e.data.key, title: e.data.key };
      const pd = pend.current && pend.current.key === e.data.key ? pend.current.state : null;
      pend.current = null;
      const nm = '«' + f.title + '»';
      const act = pd === 'open' ? '👁 Показано ' + nm : pd === 'closed' ? '🙈 Сховано ' + nm : pd === 'inherit' ? '↩ ' + nm + ' — знову як усім' : nm;
      if (e.data.found) setLast(act + ' — блок блимає жовтим у телефоні.' + (pd === 'closed' ? ' Він лишився, бо в ньому є інші дозволені функції.' : ''));
      else if (IN_STUDIO.test(f.key) && f.eff_vis === false) setLast(act + (pd ? '. У' : ': у') + ' телефоні цього блоку ' + (pd ? 'більше ' : '') + 'немає — функція цьому юзеру схована.');
      else setLast(act + (pd ? '. У' : ': у') + ' Студії окремого блоку немає. Де це в юзера: ' + helpFor(f).where + '.');
    };
    window.addEventListener('message', on);
    return () => window.removeEventListener('message', on);
  }, []);

  const pick = (key) => {
    setPicked(key);
    const w = frame.current && frame.current.contentWindow;
    if (w) w.postMessage({ type: 'st-flash', key }, window.location.origin);
    else {   // телефона немає (Студії в юзера немає) — кажемо, де функція живе
      const f = (rowsRef.current || []).find(x => x.key === key) || { key, title: key };
      setLast('«' + f.title + '». Де це в юзера: ' + helpFor(f).where + '.');
    }
  };

  // нові дозволи — у телефон без перезавантаження; шлемо, доки Студія не підтвердить (вона могла ще вантажитись)
  const pushFeat = (feat, flashKey) => {
    const id = ++seq.current;
    let iv = null, t = null;
    const stop = () => { clearInterval(iv); clearTimeout(t); window.removeEventListener('message', on); };
    function on(e) {
      if (e.origin !== window.location.origin || !e.data || e.data.type !== 'st-feat-ok' || e.data.id !== id) return;
      stop();
      if (flashKey) pick(flashKey);
    }
    const send = () => {
      if (id !== seq.current) { stop(); return; }
      const w = frame.current && frame.current.contentWindow;
      w && w.postMessage({ type: 'st-feat', feat, id }, window.location.origin);
    };
    window.addEventListener('message', on);
    t = setTimeout(stop, 12000);
    send(); iv = setInterval(send, 700);
  };

  // сторінка щойно змінила функцію (change.n) → перечитати дозволи юзера й показати в телефоні
  const firstChange = useRef(true);
  useEffect(() => {
    if (firstChange.current) { firstChange.current = false; return; }
    if (!change || !change.key) return;
    pend.current = { key: change.key, state: change.state }; setPicked(change.key);
    fetchDemo().then(d => {
      const had = !!(demo && demo.studio);
      setDemo(d);
      if (d && d.studio && had) pushFeat((d.sessions && d.sessions.feat) || {}, change.key);
      else if (d && d.studio) setN(x => x + 1);
      else pick(change.key);
    }).catch(() => {});
  }, [change && change.n]);

  const u = demo && demo.user;
  const acc = !u ? '' : u.kind === 'unlimited' ? '♾ безліміт' : u.kind === 'paid' ? '✅ доступ до ' + fmtDate(u.until)
    : u.kind === 'trial' ? '🎟 тріал до ' + fmtDate(u.until) : '⛔ доступу немає' + (u.until ? ' (закінчився ' + fmtDate(u.until) + ')' : '');
  const canWork = !!(demo && demo.studio && u && u.kind !== 'none');
  const W = devW * scale + 12;

  return (
    <Box data-usp="1" ref={rowEl} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
      {/* ── ліворуч: функції цього юзера ── */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <FeatureLegend />
        <FeatureRows rows={rows} onSet={onSet} busyKey={busyKey} onPick={pick} picked={picked} />
      </Box>

      {/* ── праворуч: телефон юзера, стоїть на місці під час гортання ── */}
      <Box data-usp-side="1" ref={sideEl} sx={{ flex: 'none', width: W, alignSelf: 'flex-start', willChange: 'transform' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: .6, mb: .6, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: C.txt, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            📱 Так бачить {who}
          </Typography>
          {DEVICES.map(([ic, t], i) => (
            <Button key={t} size="small" title={t} data-usp-dev={i} onClick={() => setDev(i)}
              sx={{ minWidth: 0, px: .7, py: 0, fontSize: 12, color: dev === i ? '#04121a' : C.dim, bgcolor: dev === i ? C.neon : 'transparent',
                    border: `1px solid ${dev === i ? C.neon : C.line}`, '&:hover': { bgcolor: dev === i ? C.neon : 'rgba(0,229,160,.08)' } }}>{ic}{i === 2 ? 's' : ''}</Button>
          ))}
          <Button size="small" title="Перезавантажити телефон" data-usp-reload="1" onClick={() => { fetchDemo().then(setDemo).catch(() => {}); setN(x => x + 1); }}
            sx={{ minWidth: 0, px: .7, py: 0, fontSize: 12, color: C.neonTxt, border: `1px solid ${C.line}` }}>⟳</Button>
        </Box>
        {u && (
          <Typography data-usp-status={canWork ? 'ok' : 'no'} sx={{ fontSize: 11.5, mb: .6, color: canWork ? C.neonTxt : C.gold }}>
            {demo.studio ? '🎬 Студія є · ' : '🎬 Студії немає · '}{acc}{u.partner ? ' · партнер «' + u.partner + '»' : ' · без партнера'}
          </Typography>
        )}
        <Typography data-usp-last="1" sx={{ fontSize: 11.5, color: last ? C.gold : C.dim, mb: .8, minHeight: 34, lineHeight: 1.35 }}>
          {last || 'Натисніть «Показувати» чи «Сховати» на картці — телефон зміниться одразу. «📍 Де це в телефоні» покаже блок функції.'}
        </Typography>
        <Box sx={{ width: W, height: devH * scale + 12, boxSizing: 'border-box', borderRadius: '30px', border: '6px solid #1c2530',
                   overflow: 'hidden', boxShadow: '0 10px 40px rgba(0,0,0,.5)', bgcolor: '#000' }}>
          {demo && demo.studio ? (
            <iframe key={n + '-' + dev} ref={frame} title="Студія v-5 очима юзера" data-usp-frame="1"
                    src={`/miniapp/studio?demo=${demo.user.partner_id}&u=${userId}&v=${n}`}
                    style={{ width: devW, height: devH, border: 0, display: 'block', transform: `scale(${scale})`, transformOrigin: '0 0' }} />
          ) : (
            <Box data-usp-none={demo ? demo.why : (demo === false ? 'error' : 'loading')}
                 sx={{ height: '100%', boxSizing: 'border-box', p: 2.5, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 1.2, bgcolor: '#0c141c' }}>
              {demo === null && <Typography sx={{ fontSize: 13, color: C.dim, textAlign: 'center' }}>Завантажую телефон…</Typography>}
              {demo === false && <Typography sx={{ fontSize: 13, color: C.coral, textAlign: 'center' }}>Не вдалося завантажити прев'ю. Натисніть ⟳.</Typography>}
              {demo && demo.why === 'no_partner' && (<>
                <Typography sx={{ fontSize: 15, fontWeight: 800, color: C.gold, textAlign: 'center' }}>🎬 Студії (v-5) у цього юзера немає</Typography>
                <Typography sx={{ fontSize: 12.5, color: C.txt, lineHeight: 1.5 }}>
                  Це ваш прямий клієнт, не учень партнера. Після відео бот дає йому «✨ Зі спецефектами» і «🎬 Без спецефектів», а Студію показує лише учням партнера.
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: C.neonTxt, lineHeight: 1.5 }}>
                  Дати Студію: угорі цієї сторінки «🧭 Версії» → v-5 → «✅ Відкрити» (теми й функції - як у партнера-еталону). Телефон оновиться сам.
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: C.dim, lineHeight: 1.5 }}>
                  Картки ліворуч працюють і без телефона; «📍» скаже словами, де функція в юзера.
                </Typography>
              </>)}
              {demo && demo.why === 'hidden' && (<>   {/* [D-691] */}
                <Typography sx={{ fontSize: 15, fontWeight: 800, color: C.gold, textAlign: 'center' }}>🎬 Студію цьому юзеру сховано</Typography>
                <Typography sx={{ fontSize: 12.5, color: C.txt, lineHeight: 1.5 }}>
                  Ви сховали йому Студію особисто. Повернути: угорі «🧭 Версії» → v-5 → «↩ Як усім» або «✅ Відкрити».
                </Typography>
              </>)}
              {demo && demo.why === 'partner_off' && (<>
                <Typography sx={{ fontSize: 15, fontWeight: 800, color: C.gold, textAlign: 'center' }}>🎬 Студію зараз не видно</Typography>
                <Typography sx={{ fontSize: 12.5, color: C.txt, lineHeight: 1.5 }}>
                  Юзер — учень партнера «{u && u.partner}», але в цього партнера Студія вимкнена або сам партнер не працює. Увімкнути: «🤝 Партнери» → цей партнер.
                </Typography>
              </>)}
            </Box>
          )}
        </Box>
        {demo && demo.studio && (   /* [D-763] OWNER 28.10: «мені треба 100% бачити те саме, що бачить учень» */
          <Typography data-usp-same="1" sx={{ fontSize: 11.5, color: C.dim, mt: .8, lineHeight: 1.45 }}>
            ✅ Телефон отримує дані тим самим шляхом, що й сам юзер, - нові функції з'являються тут самі. Інакше лише: мова (ваша, прапорець
            у Студії), демо-ролик, коли в юзера зараз немає відео, і дії - тут нічого не зберігається й не запускається. Збіг щодня перевіряє звіряльник.
          </Typography>
        )}
      </Box>
    </Box>
  );
}
